/** How many digits month, day and year each hold, in the order the field shows them. */
const PART_LENGTHS = [2, 2, 4] as const;
const SEPARATOR = '/';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TYPED_DAY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const digitsOf = (text: string): string => text.replace(/\D/g, '');

/** `2026-07-01` as `07/01/2026`; anything else as ''. */
export const toDisplay = (iso: string): string => {
  const match = ISO_DAY.exec(iso);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : '';
};

/**
 * A complete date that names a real day, as `YYYY-MM-DD`; null for 02/30 or a half-typed one.
 * A one-digit month or day counts, so `7/1/2026` is the first of July.
 */
export const toIso = (text: string): string | null => {
  const match = TYPED_DAY.exec(text);
  if (!match) return null;

  const [, month = '', day = '', year = ''] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const real =
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day);

  return real ? `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}` : null;
};

export interface DateEdit {
  text: string;
  /** Where the caret goes in `text`, so it stays where the person was typing. */
  caret: number;
}

/** The caret's place in `text` after `count` digits; at the start when there are none. */
const afterDigits = (text: string, count: number): number => {
  if (count === 0) return 0;

  let seen = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (/\d/.test(text.charAt(index))) seen += 1;
    if (seen === count) return index + 1;
  }

  return text.length;
};

/** A part that has just been filled hands the caret on past its slash, so typing runs on. */
const skipFilledSeparator = (text: string, caret: number, partFilled: boolean): number =>
  partFilled && text.charAt(caret) === SEPARATOR ? caret + 1 : caret;

/** No slash typed yet: the digits are laid out as MM/DD/YYYY as they come, `0701` reading `07/01`. */
const editDigits = (raw: string, caret: number, grew: boolean): DateEdit => {
  const digits = digitsOf(raw).slice(0, 8);
  const before = Math.min(digitsOf(raw.slice(0, caret)).length, digits.length);
  const text = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)]
    .filter(Boolean)
    .join(SEPARATOR);
  const at = afterDigits(text, before);

  return { text, caret: skipFilledSeparator(text, at, grew && (before === 2 || before === 4)) };
};

/**
 * Slashes already there: month, day and year are edited apart, so changing one never shifts the
 * others. A full part takes a new digit in place of the one after the caret, the way a paper
 * form is overwritten; typed past its end, the digit carries into the next part.
 */
const editParts = (raw: string, caret: number, grew: boolean): DateEdit => {
  const parts = raw.split(SEPARATOR).slice(0, PART_LENGTHS.length).map(digitsOf);
  const head = raw.slice(0, caret);
  let part = Math.min(head.split(SEPARATOR).length - 1, PART_LENGTHS.length - 1);
  let offset = digitsOf(head.slice(head.lastIndexOf(SEPARATOR) + 1)).length;

  for (let index = 0; index < parts.length; index += 1) {
    const digits = parts[index] ?? '';
    const length = PART_LENGTHS[index] ?? 0;
    if (digits.length <= length) continue;

    const hasNext = index < PART_LENGTHS.length - 1;
    const typedHere = part === index;

    if (typedHere && offset > length && hasNext) {
      // Typed past the end of a full part: what spills over starts the next one.
      const spill = digits.slice(length);
      const next = parts[index + 1] ?? '';
      parts[index] = digits.slice(0, length);
      parts[index + 1] = spill + next.slice(spill.length);
      part = index + 1;
      offset = spill.length;
    } else if (typedHere) {
      parts[index] = (digits.slice(0, offset) + digits.slice(offset + 1)).slice(0, length);
    } else {
      parts[index] = digits.slice(0, length);
    }
  }

  const text = parts.join(SEPARATOR);
  const start = parts.slice(0, part).reduce((sum, digits) => sum + digits.length + 1, 0);
  const at = start + Math.min(offset, parts[part]?.length ?? 0);
  const partFilled = grew && offset === PART_LENGTHS[part];

  return { text, caret: skipFilledSeparator(text, at, partFilled) };
};

/**
 * What the field shows after an edit, and where its caret goes. `raw` is the input's value as
 * the browser left it, `caret` the browser's caret in it, and `grew` whether a character was
 * added rather than taken away.
 */
export const editDate = (raw: string, caret: number, grew: boolean): DateEdit =>
  raw.includes(SEPARATOR) ? editParts(raw, caret, grew) : editDigits(raw, caret, grew);
